import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { login } from "../../redux/auth/authSlice";
import authService from "../../redux/auth/authService";
import { getAdminHomeRoute } from "../../constants/adminAccess";
import { Formik } from "formik";
import toast from "react-hot-toast";
import * as yup from "yup";
import { LoginModel } from "../../models/login.model";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { LuShield, LuUser, LuLock } from "react-icons/lu";
import headerLogo from "../../assets/images/Headerlogo_black.svg";

const OFF_WHITE = "#F1EBEB";
const DARK_NAVY = "#051525";
const DARK_NAVY_LIGHT = "#4a6278";
const BLUE = "#1E3A5F";
const BTN_LOGIN = "#1f3b64";
const BTN_HOVER = "#162C47";
const BTN_DISABLED = "#cfcaca";
const CHECKBOX_BLUE = "#1E3A5F";

const INPUT_FOCUS_GLOW =
  "0 0 0 3px rgba(30, 58, 95, 0.12), 0 4px 14px rgba(30, 58, 95, 0.18)";
const BTN_GLOW = "0 4px 18px rgba(31, 59, 100, 0.35)";

const INPUT_BORDER_DEFAULT = "#d0dff0";

const inputBaseStyle: React.CSSProperties = {
  borderWidth: "2px",
  borderStyle: "solid",
  borderColor: INPUT_BORDER_DEFAULT,
  boxShadow: "none",
  transition: "border-color 0.2s ease, box-shadow 0.2s ease",
};

const applyInputFocus = (el: HTMLInputElement) => {
  el.style.borderColor = BLUE;
  el.style.boxShadow = INPUT_FOCUS_GLOW;
};

const applyInputBlur = (el: HTMLInputElement) => {
  el.style.borderColor = INPUT_BORDER_DEFAULT;
  el.style.boxShadow = "none";
};

const loginSchema = yup.object({
  userId: yup.string().required("User ID is required."),
  password: yup.string().required("Password is required."),
});

const loginValues = {
  userId: "",
  password: "",
};

const Login = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(true);
  const [notificationConsent, setNotificationConsent] = useState(true);
  const refreshToken = useSelector((state: any) => state?.auth?.refreshToken);
  const accessToken = useSelector((state: any) => state?.auth?.accessToken);

  useEffect(() => {
    if (refreshToken && accessToken) {
      localStorage.setItem("refreshToken", refreshToken);
      localStorage.setItem("accessToken", accessToken);
    }
  }, [refreshToken, accessToken]);

  const onLogin = async (values: LoginModel) => {
    if (!termsAccepted) {
      toast.error("Please accept the Terms & Conditions.");
      return;
    }
    if (!notificationConsent) {
      toast.error("Please authorize notifications to continue.");
      return;
    }

    const res = await dispatch(login(values));
    if (res?.error?.message) {
      toast.error("Invalid user id or passsword.");
      return;
    }
    toast.success("Logged In Successfully!");
    if (res?.payload?.data?.userType === 4) {
      navigate(getAdminHomeRoute(authService.getAumUserId()));
    } else {
      navigate("/dashboard");
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ backgroundColor: OFF_WHITE }}>
      {/* Header */}
      <header className="bg-[#000000] px-6 md:px-10 py-4 flex items-center justify-between shrink-0">
        <Link to="/">
          <img
            src={headerLogo}
            alt="AUM Sampann"
            className="h-10 md:h-12 w-auto object-contain"
            style={{
              filter:
                "brightness(0) saturate(100%) invert(94%) sepia(6%) saturate(500%) hue-rotate(314deg) brightness(103%) contrast(92%)",
            }}
          />
        </Link>
        <div className="flex items-center gap-2" style={{ color: OFF_WHITE }}>
          <LuShield className="w-5 h-5" />
          <span className="text-sm md:text-base font-medium">Admin Portal</span>
        </div>
      </header>

      {/* Main content */}
      <div className="flex flex-1 min-h-0 flex-col lg:flex-row">
        {/* Left panel */}
        <div
          className="hidden lg:flex lg:w-1/2 text-white relative overflow-hidden items-center justify-center"
          style={{
            background:
              "radial-gradient(ellipse 80% 70% at 50% 45%, #2a2a2a 0%, #1c1c1c 45%, #141414 100%)",
          }}
        >
          {/* Subtle concentric circle pattern */}
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: `
                radial-gradient(circle at 50% 42%, transparent 18%, rgba(255,255,255,0.03) 18.5%, transparent 19%),
                radial-gradient(circle at 50% 42%, transparent 32%, rgba(255,255,255,0.025) 32.5%, transparent 33%),
                radial-gradient(circle at 50% 42%, transparent 48%, rgba(255,255,255,0.02) 48.5%, transparent 49%),
                radial-gradient(circle at 50% 42%, transparent 65%, rgba(255,255,255,0.015) 65.5%, transparent 66%)
              `,
            }}
          />

          {/* Single centered content block */}
          <div className="relative z-10 flex flex-col items-center text-center px-12 xl:px-16 max-w-lg">
            <p className="text-lg text-white/80 font-normal mb-1">Welcome to</p>
            <h1 className="text-[2rem] xl:text-[2.25rem] font-bold leading-tight text-white mb-6">
              Sampann
            </h1>
            <p className="text-[15px] text-white/45 leading-relaxed max-w-[340px]">
              Manage investors, transactions, reports and platform operations
              with confidence and security.
            </p>
          </div>
        </div>

        {/* Right panel */}
        <div
          className="flex-1 flex flex-col items-center justify-center px-4 sm:px-8 py-10"
          style={{ backgroundColor: OFF_WHITE }}
        >
          <div className="w-full max-w-[400px] bg-white rounded-2xl shadow-[0_2px_20px_rgba(0,0,0,0.06)] px-8 py-9">
            <div className="text-left mb-7">
              <h2
                className="text-2xl font-bold"
                style={{ color: DARK_NAVY }}
              >
                Admin Login
              </h2>
              <p className="text-sm mt-1" style={{ color: DARK_NAVY_LIGHT }}>
                Authorized Personnel Only
              </p>
            </div>

            <Formik
              initialValues={loginValues}
              validationSchema={loginSchema}
              onSubmit={(values) => onLogin(values)}
            >
              {({ values, handleChange, handleSubmit, isValid }) => (
                <form
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (!isValid) {
                      toast.error("Invalid user ID or password.");
                      return;
                    }
                    handleSubmit();
                  }}
                  className="flex flex-col gap-4"
                >
                  <div>
                    <label className="block text-sm font-medium text-[#000000] mb-1.5">
                      User ID
                    </label>
                    <div className="relative">
                      <LuUser
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-[18px] h-[18px]"
                        style={{ color: BLUE }}
                      />
                      <input
                        type="text"
                        name="userId"
                        value={values.userId}
                        onChange={handleChange("userId")}
                        placeholder="Enter your User ID"
                        className="w-full pl-10 pr-4 py-3 bg-white rounded-lg text-sm text-[#000000] placeholder:text-[#9d9d9d] focus:outline-none"
                        style={inputBaseStyle}
                        onFocus={(e) => applyInputFocus(e.target)}
                        onBlur={(e) => applyInputBlur(e.target)}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#000000] mb-1.5">
                      Password
                    </label>
                    <div className="relative">
                      <LuLock
                        className="absolute left-3 top-1/2 -translate-y-1/2 w-[18px] h-[18px]"
                        style={{ color: BLUE }}
                      />
                      <input
                        type={passwordVisible ? "text" : "password"}
                        name="password"
                        value={values.password}
                        onChange={handleChange("password")}
                        placeholder="Enter your password"
                        className="w-full pl-10 pr-11 py-3 bg-white rounded-lg text-sm text-[#000000] placeholder:text-[#9d9d9d] focus:outline-none"
                        style={inputBaseStyle}
                        onFocus={(e) => applyInputFocus(e.target)}
                        onBlur={(e) => applyInputBlur(e.target)}
                      />
                      <button
                        type="button"
                        onClick={() => setPasswordVisible((v) => !v)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-[#696969]"
                      >
                        {passwordVisible ? (
                          <VisibilityOff sx={{ fontSize: 20 }} />
                        ) : (
                          <Visibility sx={{ fontSize: 20 }} />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="text-right -mt-1">
                    <Link
                      to="/forgot-password"
                      className="text-sm hover:underline"
                      style={{ color: BLUE }}
                    >
                      Forgot password?
                    </Link>
                  </div>

                  <div className="flex flex-col gap-3">
                    <label className="flex items-start gap-2.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={termsAccepted}
                        onChange={(e) => setTermsAccepted(e.target.checked)}
                        className="mt-0.5 w-4 h-4 shrink-0"
                        style={{ accentColor: CHECKBOX_BLUE }}
                      />
                      <span className="text-sm text-[#696969]">
                        I agree to the{" "}
                        <button
                          type="button"
                          onClick={() =>
                            toast("Terms & Conditions page not set up yet.")
                          }
                          className="hover:underline"
                          style={{ color: BLUE }}
                        >
                          Terms &amp; Conditions
                        </button>
                      </span>
                    </label>
                    <label className="flex items-start gap-2.5 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={notificationConsent}
                        onChange={(e) =>
                          setNotificationConsent(e.target.checked)
                        }
                        className="mt-0.5 w-4 h-4 shrink-0"
                        style={{ accentColor: CHECKBOX_BLUE }}
                      />
                      <span className="text-sm text-[#696969]">
                        I hereby authorize to send notifications on SMS, Calls,
                        RCS.
                      </span>
                    </label>
                  </div>

                  <button
                    type="submit"
                    disabled={!termsAccepted || !notificationConsent || !isValid}
                    className="w-full py-3 mt-1 font-semibold rounded-lg transition-colors disabled:cursor-not-allowed"
                    style={{
                      backgroundColor:
                        !termsAccepted || !notificationConsent || !isValid
                          ? BTN_DISABLED
                          : BTN_LOGIN,
                      color:
                        !termsAccepted || !notificationConsent || !isValid
                          ? "#888888"
                          : "#FFFFFF",
                      boxShadow:
                        !termsAccepted || !notificationConsent || !isValid
                          ? "none"
                          : BTN_GLOW,
                    }}
                    onMouseEnter={(e) => {
                      if (!e.currentTarget.disabled) {
                        e.currentTarget.style.backgroundColor = BTN_HOVER;
                        e.currentTarget.style.boxShadow =
                          "0 6px 22px rgba(31, 59, 100, 0.45)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!e.currentTarget.disabled) {
                        e.currentTarget.style.backgroundColor = BTN_LOGIN;
                        e.currentTarget.style.boxShadow = BTN_GLOW;
                      }
                    }}
                  >
                    Login
                  </button>

                </form>
              )}
            </Formik>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
